import React, { useEffect, useState } from 'react';
import { Layers, GraduationCap, BookMarked } from 'lucide-react';
import TopicIcon from './icons/TopicIcon';
import { collectionCover, collectionTopic, collectionCount, kindLabel, fetchCollectionItems } from '../../utils/educationCollections';
import { useEducationLibrary } from '../../utils/educationLibrary';

/*
 * A collection drawn as one card, wherever it appears. Same 16:9 art as a content card, with a
 * second sheet peeking out behind it so a set reads as a set, the kind in the corner and the
 * item count on the image. Rail and row variants match ContentCard's.
 */

const KIND_ICON = { series: Layers, curriculum: GraduationCap, 'reading-list': BookMarked };

const coverCache = new Map();
const useCover = (col) => {
  const [cover, setCover] = useState(col.coverURL || coverCache.get(col.id) || null);
  useEffect(() => {
    if (col.coverURL) { setCover(col.coverURL); return undefined; }
    if (coverCache.has(col.id)) { setCover(coverCache.get(col.id)); return undefined; }
    let alive = true;
    fetchCollectionItems({ ...col, items: (col.items || []).slice(0, 1) })
      .then((items) => { const c = collectionCover(col, items); coverCache.set(col.id, c); if (alive) setCover(c); })
      .catch(() => {});
    return () => { alive = false; };
  }, [col]);
  return cover;
};

const Cover = ({ col, rounded = 'rounded-xl', iconSize = 'w-8 h-8' }) => {
  const cover = useCover(col);
  const topic = collectionTopic(col);
  const color = (topic && topic.color) || 'from-gray-500 to-gray-600';
  const KindIcon = KIND_ICON[col.kind] || Layers;
  const n = collectionCount(col);
  const { completedOf } = useEducationLibrary();
  const done = (col.items || []).filter((it) => completedOf(it.id)).length;
  return (
    <div className="relative pt-1.5 px-1.5">
      {/* the sheet behind */}
      <div className={`absolute top-0 left-3 right-3 h-4 ${rounded} bg-gray-300/80 dark:bg-gray-600/80`} aria-hidden="true" />
      <div className={`relative aspect-video w-full overflow-hidden ${rounded} bg-gray-100 dark:bg-gray-800 shadow-card`}>
        {cover ? (
          <img src={cover} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover object-top" />
        ) : (
          <div className={`absolute inset-0 bg-gradient-to-br ${color} flex items-center justify-center text-white/90`}>
            <TopicIcon type={topic ? topic.icon : null} className={iconSize} />
          </div>
        )}
        <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-black/60 text-[10px] font-semibold text-white">
          <KindIcon size={10} /> {kindLabel(col.kind)}
        </span>
        <span className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded-md bg-black/65 text-[10px] font-semibold text-white tabular-nums">
          {done > 0 ? `${done} of ${n} done` : `${n} ${n === 1 ? 'item' : 'items'}`}
        </span>
        {done > 0 && done < n && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/35" aria-hidden="true">
            <div className="h-full bg-emerald-500" style={{ width: `${Math.round((done / n) * 100)}%` }} />
          </div>
        )}
      </div>
    </div>
  );
};

const Meta = ({ col, className = '' }) => {
  const topic = collectionTopic(col);
  const bits = [topic && topic.label].filter(Boolean);
  if (!bits.length) return null;
  return <div className={`text-xs text-gray-500 dark:text-gray-400 truncate ${className}`}>{bits.join(' · ')}</div>;
};

const CollectionCard = ({ collection: col, variant = 'rail', onOpen, width = 'w-52', className = '' }) => {
  if (variant === 'row') {
    return (
      <button
        type="button"
        onClick={() => onOpen(col)}
        className={`w-full text-left bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-3 shadow-card hover:border-emerald-500 dark:hover:border-emerald-400 hover:shadow-card-hover transition-all ${className}`}
      >
        <div className="flex items-center gap-3">
          <div className="w-28 flex-shrink-0 -mt-1">
            <Cover col={col} rounded="rounded-lg" iconSize="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-[15px] font-semibold leading-snug text-gray-900 dark:text-white line-clamp-2">{col.title}</h3>
            <Meta col={col} className="mt-1" />
          </div>
        </div>
      </button>
    );
  }
  return (
    <button type="button" onClick={() => onOpen(col)} className={`snap-start flex-shrink-0 ${width} text-left group ${className}`}>
      <Cover col={col} />
      <div className="pt-2 px-1.5">
        <h3 className="text-[13.5px] font-semibold leading-snug text-gray-900 dark:text-white line-clamp-2">{col.title}</h3>
        <Meta col={col} className="mt-0.5 text-[11px]" />
      </div>
    </button>
  );
};

export const CollectionRail = ({ collections, onOpen, width }) => (
  <div className="flex gap-3 overflow-x-auto pb-2 -mx-6 px-6 scrollbar-hide snap-x snap-mandatory scroll-px-6">
    {collections.map((c) => <CollectionCard key={c.id} collection={c} onOpen={onOpen} width={width} />)}
  </div>
);

export default CollectionCard;
